'use client';
import { useScopedI18n } from '@locales/client';

import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';
import { MultiAccountWarningModal } from '@/features/multi-account-warning-modal';
import { formatTimestamp } from '@/shared/libs/format-timestamp';
import { useIsAuth } from '@/shared/providers/user-provider/use-user';
import { BodyTextRegularB, HeadingBoldH7 } from '@/shared/ui/typography2';
import { useVisibleState } from '@/shared/hooks/useVisibleState';
import { ShowOnMobile, ShowOnDesktop } from '@/shared/ui/responsive';
import AuthButtons from '@/shared/ui/auth-buttons';
import { Button } from '@/shared/ui/form/button/button';

import { useCashback } from './model/hooks';
import { InfoList, CashbackBanner, HistoryModal, Details } from './ui';
import {
  ButtonText,
  HistoryButtonDesktop,
  HistoryButtonDesktopContainer,
  HistoryButtonDesktopImg,
  MobileCashBackHistory,
  RootContainer,
  Wrapper,
  ExpiryBox,
  AuthButtonsBleed,
} from './styles';

const CashbackPage = () => {
  const isAuth = useIsAuth();
  const t = useScopedI18n('cashback');
  const { open, visible, close } = useVisibleState(false);
  const { onPopupShow } = useConfirmDataPrompt();

  const { data, claimRewardsTrigger, isClaimingLoading } = useCashback();
  const isDisabled = !isAuth || isClaimingLoading;

  const getCashbackHandler = async () => {
    if (!isAuth) {
      onPopupShow();
      return;
    }
    try {
      await claimRewardsTrigger();
    } catch (error) {
      console.error('Error claiming reward:', error);
    }
  };

  return (
    <>
      <RootContainer>
        <CashbackBanner />

        <Wrapper>
          {!isAuth ? (
            <>
              <AuthButtonsBleed>
                <AuthButtons />
              </AuthButtonsBleed>
              <InfoList />
            </>
          ) : (
            <>
              <Details />
              <ShowOnDesktop>
                <HistoryButtonDesktopContainer>
                  {data?.tempCashback?.expire && (
                    <ExpiryBox>
                      <BodyTextRegularB color="#AFAFAF">
                        {t('cashback_valid_until')}
                      </BodyTextRegularB>
                      <HeadingBoldH7>
                        {formatTimestamp(data.tempCashback.expire)}
                      </HeadingBoldH7>
                    </ExpiryBox>
                  )}
                  <HistoryButtonDesktop onClick={open}>
                    <HistoryButtonDesktopImg />
                    <BodyTextRegularB>
                      {t('history_of_transactions')}
                    </BodyTextRegularB>
                  </HistoryButtonDesktop>
                </HistoryButtonDesktopContainer>
              </ShowOnDesktop>
              <InfoList />

              <ShowOnMobile>
                <MobileCashBackHistory>
                  <Button
                    variant={isDisabled ? 'gray' : 'orangeGradient'}
                    onClick={getCashbackHandler}
                    disabled={isDisabled}
                  >
                    <ButtonText>{t('get_cashback')}</ButtonText>
                  </Button>
                  <Button variant={'gray'} onClick={open}>
                    <ButtonText>{t('history')}</ButtonText>
                  </Button>
                </MobileCashBackHistory>
              </ShowOnMobile>
            </>
          )}
        </Wrapper>
      </RootContainer>

      {isAuth && (
        <>
          <HistoryModal visible={visible} onClose={close} />
          <MultiAccountWarningModal />
        </>
      )}
    </>
  );
};

export { CashbackPage };
